<template>
  <div class="db-tw-mb-9">
    <DbCheckbox
      :modelValue="enableGuestRooms"
      v-on:update:model-value="$emit('update:enableGuestRooms', $event)"
    >
      {{ label }}
    </DbCheckbox>
    <div
      v-if="enableGuestRooms"
      class="db-tw-mt-6 db-tw-mb-9 md:db-tw-mb-0 db-tw-grid db-tw-grid-cols-2 db-tw-gap-y-3 md:db-tw-grid-cols-3 db-tw-gap-3"
    >
      <div>
        <DbDatepicker
          :date="checkIn"
          :minDate="subDays(new Date(), 0)"
          v-on:update:date="$emit('update:checkIn', $event)"
        >
          {{ labelCheckIn }}
          <template
            v-if="v$.fieldCheckIn.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldCheckIn.$errors" />
          </template>
        </DbDatepicker>
      </div>
      <div>
        <DbInput
          :modelValue="nbNights"
          :inputAttrs="{
            type: 'number',
            min: 0,
          }"
          v-on:update:model-value="$emit('update:nbNights', $event)"
        >
          {{ labelNbNights }}
          <template
            v-if="v$.fieldNbNights.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldNbNights.$errors" />
          </template>
        </DbInput>
      </div>
      <div>
        <DbInput
          :modelValue="nbRooms"
          :inputAttrs="{
            type: 'number',
            min: 0,
          }"
          v-on:update:model-value="$emit('update:nbRooms', $event)"
        >
          {{ labelNbRooms }}
          <template
            v-if="v$.fieldNbRooms.$error"
            v-slot:error
          >
            <DbRfpErrors :errors="v$.fieldNbRooms.$errors" />
          </template>
        </DbInput>
      </div>
    </div>
    <div v-if="enableGuestRooms">
      <div>
        <RfpRepeatableDate
          v-if="checkIn"
          v-model="alternateDates"
          :label="labelAlternativeCheckIn"
          :limit="nbAlternativeDates"
          :labelAdd="labelAddAlternativeCheckIn"
          :labelRemove="labelRemoveAlternativeCheckIn"
        />
      </div>
    </div>
    <div
      v-if="showGuestRequest && enableGuestRooms"
      class="db-tw-my-9"
    >
      <DbTextarea
        :modelValue="request"
        :inputAttrs="{ rows: 3 }"
        v-on:update:model-value="$emit('update:request', $event)"
      >
        {{ labelRequest }}
        <template
          v-if="v$.fieldRequest.$error"
          v-slot:error
        >
          <DbRfpErrors :errors="v$.fieldRequest.$errors" />
        </template>
      </DbTextarea>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { numeric, maxLength } from '@vuelidate/validators'
import { subDays } from 'date-fns'

const props = withDefaults(
  defineProps<{
    enableGuestRooms?: boolean
    checkIn?: Date
    nbNights?: string
    nbRooms?: string
    alternativeCheckIn1?: Date
    alternativeCheckIn2?: Date
    request?: string
    label: string
    labelCheckIn: string
    labelNbNights: string
    labelNbRooms: string
    labelAlternativeCheckIn: string
    labelAddAlternativeCheckIn: string
    labelRemoveAlternativeCheckIn: string
    labelRequest: string
    showGuestRequest: boolean
    nbAlternativeDates: number
  }>(),
  {
    enableGuestRooms: false,
    checkIn: undefined,
    nbNights: '',
    nbRooms: '',
    alternativeCheckIn1: undefined,
    alternativeCheckIn2: undefined,
    request: '',
  },
)

const emit = defineEmits<{
  (e: 'update:enableGuestRooms', enabled: boolean): void
  (e: 'update:checkIn', checkIn: Date): void
  (e: 'update:nbNights', nbNights: string | number): void
  (e: 'update:nbRooms', nbRooms: string | number): void
  (e: 'update:alternativeCheckIn1', alternativeCheckIn1: Date | undefined): void
  (e: 'update:alternativeCheckIn2', alternativeCheckIn2: Date | undefined): void
  (e: 'update:request', request: string | number): void
}>()

const alternateDates = computed({
  get() {
    const dates: Date[] = []
    if (props.alternativeCheckIn1) {
      dates.push(props.alternativeCheckIn1)
    }
    if (props.alternativeCheckIn2) {
      dates.push(props.alternativeCheckIn2)
    }
    return dates
  },
  set(newValue) {
    emit('update:alternativeCheckIn1', newValue[0])
    emit('update:alternativeCheckIn2', newValue[1])
  },
})

const state = computed(() => {
  return {
    fieldCheckIn: props.checkIn,
    fieldNbNights: props.nbNights,
    fieldNbRooms: props.nbRooms,
    fieldAlternativeCheckIn1: props.alternativeCheckIn1,
    fieldAlternativeCheckIn2: props.alternativeCheckIn1,
    fieldRequest: props.request,
  }
})
const rules = computed(() => {
  return {
    fieldCheckIn: {},
    fieldNbNights: { numeric },
    fieldNbRooms: { numeric },
    fieldAlternativeCheckIn1: {},
    fieldAlternativeCheckIn2: {},
    fieldRequest: { maxLengthValue: maxLength(3000) },
  }
})
const v$ = useVuelidate(rules, state, { $autoDirty: true })
</script>
